import React, { useState, useMemo } from "react";
import {
  DndContext,
  DragStartEvent,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import toast from "react-hot-toast";
import KanbanColumn from "./KanbanColumn";
import KanbanCard, { KanbanCardDisplay, TaskWithUser } from "./KanbanCard";
import { ClockIcon } from "@heroicons/react/24/outline";

const COLUMNS: {
  key: TaskWithUser["status"];
  label: string;
  accent: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}[] = [
  { key: "pending", label: "Openstaand", accent: "#EAB308" },
  { key: "in_progress", label: "Bezig", accent: "#3B82F6" },
  { key: "completed", label: "Voltooid", accent: "#16A34A", collapsible: true, defaultCollapsed: false },
  { key: "cancelled", label: "Geannuleerd", accent: "#9CA3AF", collapsible: true, defaultCollapsed: true },
];

interface KanbanBoardProps {
  tasks: TaskWithUser[];
  currentUserId?: string;
  canChangeStatus: (task: TaskWithUser) => boolean;
  onUpdateTaskStatus: (taskId: string, status: TaskWithUser["status"]) => Promise<void>;
  onCompleteTask: (taskId: string) => Promise<void>;
  onClaimTask: (taskId: string) => Promise<void>;
  onUnclaimTask: (taskId: string) => Promise<void>;
  onClickCard: (task: TaskWithUser) => void;
}

const PRIORITY_ORDER: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export default function KanbanBoard({
  tasks,
  currentUserId,
  canChangeStatus,
  onUpdateTaskStatus,
  onCompleteTask,
  onClaimTask,
  onUnclaimTask,
  onClickCard,
}: KanbanBoardProps) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  // Group and sort tasks by column
  const columnTasks = useMemo(() => {
    const grouped: Record<TaskWithUser["status"], TaskWithUser[]> = {
      pending: [],
      in_progress: [],
      completed: [],
      cancelled: [],
    };

    for (const task of tasks) {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    }

    // Sort active columns by priority then due date
    for (const status of ["pending", "in_progress"] as const) {
      grouped[status].sort((a, b) => {
        const pa = PRIORITY_ORDER[a.priority] ?? 3;
        const pb = PRIORITY_ORDER[b.priority] ?? 3;
        if (pa !== pb) return pa - pb;
        if (a.due_date && b.due_date)
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        if (a.due_date) return -1;
        if (b.due_date) return 1;
        return 0;
      });
    }

    // Sort completed/cancelled by most recent first
    for (const status of ["completed", "cancelled"] as const) {
      grouped[status].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    }

    return grouped;
  }, [tasks]);

  const activeDragTask = activeDragId
    ? tasks.find((t) => t.id === activeDragId) ?? null
    : null;

  function handleDragStart(event: DragStartEvent) {
    setActiveDragId(event.active.id as string);
  }

  async function handleDragEnd(event: DragEndEvent) {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const taskId = active.id as string;
    const newStatus = over.id as TaskWithUser["status"];
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === newStatus) return;

    // Permission check
    if (!canChangeStatus(task)) {
      toast.error("Je hebt geen toestemming om deze taak te wijzigen");
      return;
    }

    // Open-to-all unclaimed task dragged to in_progress → claim it
    if (
      task.assignment_type === "open_to_all" &&
      !task.claimed_by &&
      newStatus === "in_progress"
    ) {
      await onClaimTask(taskId);
      return;
    }

    // Dragged to completed → use completeTask RPC (handles audit logging)
    if (newStatus === "completed") {
      await onCompleteTask(taskId);
      return;
    }

    // Default: direct status update
    await onUpdateTaskStatus(taskId, newStatus);
  }

  function handleDragCancel() {
    setActiveDragId(null);
  }

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200 shadow-sm">
        <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Geen taken gevonden</h3>
        <p className="mt-1 text-sm text-gray-500">
          Pas je filters aan of maak een nieuwe taak aan.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => (
          <KanbanColumn
            key={col.key}
            columnKey={col.key}
            label={col.label}
            accentColor={col.accent}
            count={columnTasks[col.key].length}
            collapsible={col.collapsible}
            defaultCollapsed={col.defaultCollapsed}
          >
            {columnTasks[col.key].map((task) => (
              <KanbanCard
                key={task.id}
                task={task}
                canDrag={canChangeStatus(task)}
                onClickCard={onClickCard}
                onClaimTask={onClaimTask}
                onUnclaimTask={onUnclaimTask}
                currentUserId={currentUserId}
              />
            ))}
          </KanbanColumn>
        ))}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDragTask && (
          <div className="w-[280px]">
            <KanbanCardDisplay
              task={activeDragTask}
              canDrag={false}
              onClickCard={() => {}}
              isDragOverlay
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
